"use client";

import { useEffect } from "react";
import { toast } from "sonner";
import {
  LOCAL_VERSION,
  isVersionMismatch,
  hasReloadedFor,
  markReloadedFor,
} from "@/lib/version-check";

/**
 * Version beacon (#237) — integração da detecção de build antigo.
 *
 * Duas fontes anunciam a versão do servidor:
 *  1. Socket `server:version` no connect (deploy reinicia a API → todos os
 *     sockets reconectam → detecção imediata) — o handler do use-chat-socket
 *     chama `reportServerVersion`.
 *  2. Poll no `visibilitychange` (aba volta a ficar visível → GET /health)
 *     — cobre aba que dormiu com o socket morto.
 *
 * Reação ao mismatch: aba oculta → reload imediato e silencioso; aba
 * visível → toast persistente com botão "Recarregar" + reload automático
 * quando ela ficar oculta (não interrompe o agente digitando). Guard
 * anti-loop por sessionStorage: se o reload não resolveu (deploy pela
 * metade), não loopa — fica só o toast.
 */

const API_URL = process.env.NEXT_PUBLIC_API_URL || "/api";
const TOAST_ID = "gk-version-beacon";

// Estado module-level: o beacon é um singleton por aba — o socket reconecta
// a cada refresh de token (~15min) e re-anuncia a mesma versão; deduplicar
// aqui evita re-tostar/re-agendar a cada reconnect.
let pendingServerVersion: string | null = null;
let hiddenListenerArmed = false;

function reloadFor(version: string) {
  markReloadedFor(window.sessionStorage, version);
  window.location.reload();
}

/** Chamado pelo socket handler (`server:version`) e pelo poll do /health. */
export function reportServerVersion(serverVersion: unknown): void {
  if (typeof serverVersion !== "string") return;
  if (!isVersionMismatch(LOCAL_VERSION, serverVersion)) return;
  if (pendingServerVersion === serverVersion) return; // já tratado
  pendingServerVersion = serverVersion;

  const canAutoReload = !hasReloadedFor(window.sessionStorage, serverVersion);

  if (document.hidden && canAutoReload) {
    reloadFor(serverVersion);
    return;
  }

  toast.info("Nova versão da plataforma disponível", {
    id: TOAST_ID,
    duration: Infinity,
    description: "Recarregue para receber as correções mais recentes.",
    action: {
      label: "Recarregar",
      onClick: () => reloadFor(serverVersion),
    },
  });

  // Auto-reload silencioso quando o agente sair da aba (não interrompe quem
  // está digitando). Um listener só por aba, guard re-checado na hora.
  if (canAutoReload && !hiddenListenerArmed) {
    hiddenListenerArmed = true;
    const onHidden = () => {
      if (!document.hidden) return;
      const version = pendingServerVersion;
      if (version && !hasReloadedFor(window.sessionStorage, version)) {
        reloadFor(version);
      }
    };
    document.addEventListener("visibilitychange", onHidden);
  }
}

async function pollServerVersion(): Promise<void> {
  try {
    const res = await fetch(`${API_URL}/health`, { cache: "no-store" });
    if (!res.ok) return; // 503 = banco fora; não é assunto do beacon
    const data = (await res.json()) as { version?: string };
    reportServerVersion(data.version);
  } catch {
    // rede fora — o próximo visibilitychange tenta de novo
  }
}

/**
 * Montar UMA vez no layout do dashboard. Registra o poll de fallback no
 * visibilitychange (aba volta a ficar visível → confere a versão).
 */
export function useVersionBeacon(): void {
  useEffect(() => {
    const onVisible = () => {
      if (!document.hidden) void pollServerVersion();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);
}
